import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CalendarClock,
  CalendarDays,
  Image as ImageIcon,
  List,
  Loader2,
  MapPin,
  MapPinned,
  MonitorSmartphone,
  MousePointerClick,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Settings2,
  Smartphone,
  Utensils,
  Pizza,
  Beef,
  CupSoda,
  IceCreamBowl,
  Search,
  Bike,
  Clock3,
  ShoppingBag,
  Star,
  Heart,
  Sparkles,
  Trash2,
} from "lucide-react";

import useBanner from "../Services/DomainServices/useBanner";

/* =========================================================
   INITIAL FORM
========================================================= */

const EMPTY_FORM = {
  banner_name: "",
  banner_title: "",
  banner_subtitle: "",

  media_type: "IMAGE",
  media_url: "",
  thumbnail_url: "",
  background_color: "#ffffff",

  cta_text: "",
  cta_type: "NONE",
  cta_value: "",

  target_type: "GLOBAL",
  city_id: "",
  city_code: "",
  latitude: "",
  longitude: "",
  radius_km: "",

  restaurant_id: "",
  category_id: "",

  order_type: "ALL",

  platform: "ALL",
  customer_type: "ALL",

  minimum_app_version: "",
  maximum_app_version: "",

  screen_name: "HOME",
  banner_position: "HOME_TOP",

  display_order: "0",
  priority: "0",

  start_date: "",
  end_date: "",

  created_by: "",
};

/* =========================================================
   OPTIONS
========================================================= */

const MEDIA_TYPES = ["IMAGE", "ANIMATED_IMAGE", "VIDEO", "LOTTIE"];

const CTA_TYPES = [
  "NONE",
  "RESTAURANT",
  "OFFERRESTROITEM",
  "CATEGORY",
  "ITEM",
  "OFFER",
  "COUPON",
  "WEBVIEW",
  "DEEPLINK",
];

const TARGET_TYPES = ["GLOBAL", "CITY", "RADIUS"];

const ORDER_TYPES = [
  "ALL",
  "HOME_DELIVERY",
  "TAKEAWAY",
  "DINING",
  "CAR_ORDER",
  "OFFICE",
];

const PLATFORM_TYPES = ["ALL", "ANDROID", "IOS", "WEB"];

const CUSTOMER_TYPES = ["ALL", "NEW", "EXISTING"];

/* =========================================================
   COMMON UI
========================================================= */

function Section({ title, description, icon: Icon, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/70 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#01C37D]/10 text-[#01C37D]">
          <Icon size={16} />
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>

          {description && (
            <p className="mt-0.5 text-[11px] text-gray-500">{description}</p>
          )}
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, required = false, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-semibold text-gray-700">
        {label}

        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {children}

      {hint && <p className="mt-1 text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>

      <span className="max-w-[160px] truncate rounded-md bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-700">
        {value || "—"}
      </span>
    </div>
  );
}

const inputClass =
  "h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10 disabled:cursor-not-allowed disabled:bg-gray-100";

const selectClass =
  "h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10 disabled:cursor-not-allowed disabled:bg-gray-100";

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function AppBanner() {
  const { createAppBanner, fetchAppBanner, uploadAppBannerImage,deleteAppBanner } = useBanner();
  const displaySectionRef = useRef(null);
  const phonePreviewScrollRef = useRef(null);
const phonePreviewPositionRefs = useRef({});

  const [activeAppBannerTab, setActiveAppBannerTab] = useState("list");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [appBanners, setAppBanners] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaType, setMediaType] = useState("screen");
  const [imageUrl, setImageUrl] = useState("")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
const [bannerToDelete, setBannerToDelete] = useState(null);
const [deleting, setDeleting] = useState(false);

  const SCREEN_NAME_OPTIONS = [
    "HOME",
    "PROFILE",
    "SEARCH",
    "ORDERS",
    "CART",
    "WALLET",
  ];

  const BANNER_POSITION_OPTIONS = {
    HOME: [
      "HOME_HERO",
      "AFTER_HERO",
      "AFTER_CATEGORY_FILTER",
      "AFTER_FAST_DELIVERY",
      "AFTER_WHAT_ON_YOUR_MIND",
      "HOME_FOOTER",
    ],

    PROFILE: [

    ],

    SEARCH: [
      ,
    ],

    ORDERS: [

    ],
    CART: [

    ],
    WALLET: [

    ],
  };
  /* =========================================================
     GET APP BANNERS
  ========================================================= */

  const loadAppBanners = useCallback(async () => {
    setListLoading(true);

    try {
      const list = await fetchAppBanner();

      setAppBanners(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("loadAppBanners error:", error);

      setAppBanners([]);
    } finally {
      setListLoading(false);
    }
  }, [fetchAppBanner]);

  useEffect(() => {
    if (activeAppBannerTab !== "list") {
      return;
    }

    loadAppBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAppBannerTab]);

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };


      if (field === "cta_type" && value === "NONE") {
        next.cta_text = "";
        next.cta_value = "";
      }

      if (field === "target_type") {
        if (value !== "CITY") {
          next.city_id = "";
          next.city_code = "";
        }

        if (value !== "RADIUS") {
          next.latitude = "";
          next.longitude = "";
          next.radius_km = "";
        }
      }

      return next;
    });
  };

  /* =========================================================
     CTA VALUE LABEL
  ========================================================= */

  const ctaValueLabel = useMemo(() => {
    switch (form.cta_type) {


      case "CATEGORY":
        return "Category ID";

      case "ITEM":
        return "Item ID";

      case "OFFER":
        return "Offer ID / Value";

      case "COUPON":
        return "Coupon Code";

      case "WEBVIEW":
        return "Web URL";

      case "DEEPLINK":
        return "Deep Link";

      default:
        return "CTA Value";
    }
  }, [form.cta_type]);


  /* =========================================================
     RESET
  ========================================================= */

  const handleReset = () => {
    if (saving) return;

    setForm(EMPTY_FORM);
  };

  /* =========================================================
     image upload BANNER
  ========================================================= */

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file || saving || uploading) return;

    try {
      setUploading(true);

      const response = await uploadAppBannerImage(
        file,
        mediaType
      );

      console.log("UPLOAD RESPONSE:", response);

      if (!response) return;

      setImageUrl(response?.original?.file_url)

      const mediaUrl = `desktop: ${response?.images?.large?.file_url} | ${response?.images?.large?.aspect_ratio} || tablet: ${response?.images?.tablet?.file_url} |  ${response?.images?.tablet?.aspect_ratio} || mobile: ${response?.images?.mobile?.file_url} | ${response?.images?.mobile?.aspect_ratio} `

      if (!mediaUrl) {
        console.error(
          "Image URL not found in response:",
          response
        );
        return;
      }

      updateField("media_url", mediaUrl);



    } catch (error) {
      console.error(
        "handleUpload image error:",
        error
      );



    } finally {
      setUploading(false);

      // Allows selecting the same file again
      e.target.value = "";
    }
  };

  /* =========================================================
   DELETE BANNER
========================================================= */
const handleDelete = (banner) => {
  if (!banner?.pk_banner_id) return;

  setBannerToDelete(banner);
  setDeleteModalOpen(true);
};
const confirmDeleteBanner = async () => {
  if (!bannerToDelete?.pk_banner_id || deleting) return;

  try {
    setDeleting(true);

    await deleteAppBanner(bannerToDelete.pk_banner_id);

    // Remove deleted banner from UI immediately
    setAppBanners((prev) =>
      prev.filter(
        (item) =>
          item.pk_banner_id !== bannerToDelete.pk_banner_id
      )
    );

    // Close modal
    setDeleteModalOpen(false);
    setBannerToDelete(null);

  } catch (error) {
    console.error("Failed to delete banner:", error);
  } finally {
    setDeleting(false);
  }
};


  /* =========================================================
     CREATE BANNER
  ========================================================= */

  const handleSubmit = async () => {
    if (saving) return;

    setSaving(true);

    try {
      const success = await createAppBanner(form);

      if (!success) {
        return;
      }

      /* Reset form */
      setForm(EMPTY_FORM);

      /*
       * Create hone ke baad fresh list lao.
       */
      const updatedList = await fetchAppBanner();

      setAppBanners(Array.isArray(updatedList) ? updatedList : []);

      /*
       * User ko automatically
       * Banner List par le jao.
       *
       * Route change nahi hoga.
       */
      setActiveAppBannerTab("list");
    } catch (error) {
      console.error("handleSubmit App Banner error:", error);
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     PREVIEW
  ========================================================= */

  // const previewImage =
  //   form.thumbnail_url?.trim() || form.media_url?.trim() || "";
  const previewImage = imageUrl;

  const showImagePreview =
    Boolean(previewImage) &&
    (form.media_type === "IMAGE" || form.media_type === "ANIMATED_IMAGE");

const handleCreateBanner = (position = "") => {
  setForm((prev) => ({
    ...prev,
    screen_name: "HOME",
    banner_position: position || prev.banner_position || "HOME_HERO",
  }));

  setActiveAppBannerTab("create");
};

useEffect(() => {
  if (
    activeAppBannerTab !== "create" ||
    form.screen_name !== "HOME" ||
    !form.banner_position
  ) {
    return;
  }

  const scrollContainer = phonePreviewScrollRef.current;
  const target = phonePreviewPositionRefs.current[form.banner_position];

  if (!scrollContainer || !target) {
    return;
  }

  requestAnimationFrame(() => {
    const targetTop = target.offsetTop;

    scrollContainer.scrollTo({
      top: Math.max(0, targetTop - 12),
      behavior: "smooth",
    });
  });
}, [
  activeAppBannerTab,
  form.screen_name,
  form.banner_position,
]);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="rounded-2xl bg-[#f8fafc] p-3 sm:p-4">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">App Banner</h2>

        <p className="mt-0.5 text-xs text-gray-500">
          Create and manage targeted banners for the Chaafo application.
        </p>
      </div>

      {/* =====================================================
          APP BANNER SUB TABS
      ===================================================== */}

      <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-200">
        <div className="flex items-center gap-5">
          {/* LIST TAB */}

          <button
            type="button"
            onClick={() => setActiveAppBannerTab("list")}
            className={`flex items-center gap-1.5 border-b-2 px-1 py-2.5 text-sm font-semibold transition ${activeAppBannerTab === "list"
              ? "border-[#01C37D] text-[#01C37D]"
              : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
          >
            <List size={15} />
            Banner List
          </button>

          {/* CREATE TAB */}

          <button
            type="button"
            onClick={() => setActiveAppBannerTab("create")}
            className={`flex items-center gap-1.5 border-b-2 px-1 py-2.5 text-sm font-semibold transition ${activeAppBannerTab === "create"
              ? "border-[#01C37D] text-[#01C37D]"
              : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
          >
            <Plus size={15} />
            Create Banner
          </button>
        </div>

        {/* REFRESH */}

        {activeAppBannerTab === "list" && (
          <button
            type="button"
            disabled={listLoading}
            onClick={loadAppBanners}
            className="mb-1 flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[11px] font-semibold text-gray-600 transition hover:border-[#01C37D]/40 hover:bg-gray-50 hover:text-[#01C37D] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={listLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        )}
      </div>

      {/* =====================================================
          BANNER LIST
      ===================================================== */}

      {activeAppBannerTab === "list" && (
        <AppBannerList
  banners={appBanners}
  loading={listLoading}
  onCreate={handleCreateBanner}
  onDelete={handleDelete}
/>
      )}

      {/* =====================================================
          CREATE BANNER
      ===================================================== */}

      {activeAppBannerTab === "create" && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_500px]">
          {/* =================================================
              FORM LEFT
          ================================================= */}

          <div className="space-y-4">
            {/* =================================================
                BASIC INFORMATION
            ================================================= */}

            <div ref={displaySectionRef} className="scroll-mt-5">
  <Section
    title="Display & Schedule"
    description="Configure banner placement, ordering and active period."
    icon={CalendarDays}
  >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Banner Name" required>
                  <input
                    className={inputClass}
                    value={form.banner_name}
                    disabled={saving}
                    placeholder="Summer Sale Banner"
                    onChange={(e) => updateField("banner_name", e.target.value)}
                  />
                </Field>

                <Field label="Banner Title">
                  <input
                    className={inputClass}
                    value={form.banner_title}
                    disabled={saving}
                    placeholder="Get 30% Off"
                    onChange={(e) =>
                      updateField("banner_title", e.target.value)
                    }
                  />
                </Field>

                <Field label="Banner Subtitle" className="md:col-span-2">
                  <input
                    className={inputClass}
                    value={form.banner_subtitle}
                    disabled={saving}
                    placeholder="Order your favourite food today"
                    onChange={(e) =>
                      updateField("banner_subtitle", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>
              </div>

            {/* =================================================
                MEDIA
            ================================================= */}

            <Section
              title="Media"
              description="Configure banner image, animation, video or Lottie."
              icon={ImageIcon}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Media Type" required>
                  <select
                    className={selectClass}
                    value={form.media_type}
                    disabled={saving}
                    onChange={(e) => updateField("media_type", e.target.value)}
                  >
                    {MEDIA_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Background Color">
                  <div className="flex h-10 overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-[#01C37D]">
                    <input
                      type="color"
                      disabled={saving}
                      value={form.background_color || "#ffffff"}
                      onChange={(e) =>
                        updateField("background_color", e.target.value)
                      }
                      className="h-full w-12 cursor-pointer border-0 bg-transparent p-1"
                    />

                    <input
                      value={form.background_color}
                      disabled={saving}
                      onChange={(e) =>
                        updateField("background_color", e.target.value)
                      }
                      className="min-w-0 flex-1 border-0 px-3 text-sm outline-none"
                      placeholder="#ffffff"
                    />
                  </div>
                </Field>



                {/* Media URL */}
                <Field
                  label="Media URL"
                  required
                >
                  <input
                    type="text"
                    className={inputClass}
                    value={form.media_url || ""}
                    disabled={saving}
                    readOnly
                    placeholder="Media URL will appear after upload..."
                  />
                </Field>

                {/* Image Upload */}

                <Field
                  label="Image Upload"
                  required
                >
                  <div className="flex gap-2">

                    {/* Type Dropdown */}
                    <select
                      className={`${inputClass} w-2/4`}
                      value={mediaType}
                      disabled={saving || uploading}
                      onChange={(e) => setMediaType(e.target.value)}
                    >
                      <option value="screen">Screen</option>
                      <option value="hero">Hero</option>
                    </select>

                    {/* Upload Button */}
                    <label className="flex flex-1 cursor-pointer items-center justify-center rounded-md bg-[#01C37D] px-4 py-2 text-sm font-medium text-white hover:bg-[#01b873]">
                      {uploading ? "Uploading..." : "Upload Image"}

                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={saving || uploading}
                        onChange={handleUpload}
                      />
                    </label>

                  </div>
                </Field>

                <Field label="Thumbnail URL" className="md:col-span-2">
                  <input
                    className={inputClass}
                    value={form.thumbnail_url}
                    disabled={saving}
                    placeholder="https://..."
                    onChange={(e) =>
                      updateField("thumbnail_url", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

                        {/* =================================================
                DISPLAY
            ================================================= */}

            <Section
              title="Display & Schedule"
              description="Configure banner placement, ordering and active period."
              icon={CalendarDays}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Field label="Screen Name">
                  <select
                    className={inputClass}
                    value={form.screen_name}
                    disabled={saving}
                    onChange={(e) => {
                      const selectedScreen = e.target.value;

                      updateField("screen_name", selectedScreen);
                      updateField("banner_position", "");
                    }}
                  >
                    <option value="">Select Screen</option>

                    {SCREEN_NAME_OPTIONS.map((screen) => (
                      <option key={screen} value={screen}>
                        {screen
                          .toLowerCase()
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Banner Position">
                  <select
                    className={inputClass}
                    value={form.banner_position}
                    disabled={saving || !form.screen_name}
                    onChange={(e) =>
                      updateField("banner_position", e.target.value)
                    }
                  >
                    <option value="">
                      {form.screen_name
                        ? "Select Banner Position"
                        : "Select Screen First"}
                    </option>

                    {(BANNER_POSITION_OPTIONS[form.screen_name] || []).map(
                      (position) => (
                        <option key={position} value={position}>
                          {position
                            .toLowerCase()
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        </option>
                      )
                    )}
                  </select>
                </Field>

                <Field label="Display Order">
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={form.display_order}
                    disabled={saving}
                    onChange={(e) =>
                      updateField("display_order", e.target.value)
                    }
                  />
                </Field>

                <Field label="Priority">
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={form.priority}
                    disabled={saving}
                    onChange={(e) => updateField("priority", e.target.value)}
                  />
                </Field>

                <Field label="Start Date & Time">
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={form.start_date}
                    disabled={saving}
                    onChange={(e) =>
                      updateField("start_date", e.target.value)
                    }
                  />
                </Field>

                <Field label="End Date" required>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={form.end_date}
                    disabled={saving}
                    onChange={(e) => updateField("end_date", e.target.value)}
                  />
                </Field>

                <Field
                  label="Created By"
                  hint="Blank chhodne par current admin ID automatically jayega."
                  className="md:col-span-2"
                >
                  <input
                    className={inputClass}
                    value={form.created_by}
                    disabled={saving}
                    placeholder="Auto from logged-in admin"
                    onChange={(e) => updateField("created_by", e.target.value)}
                  />
                </Field>
              </div>
            </Section>

            {/* =================================================
                CTA
            ================================================= */}

            <Section
              title="Call To Action"
              description="Choose what happens when the banner is tapped."
              icon={MousePointerClick}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="CTA Type">
                  <select
                    className={selectClass}
                    value={form.cta_type}
                    disabled={saving}
                    onChange={(e) => updateField("cta_type", e.target.value)}
                  >
                    {CTA_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </Field>

                {form.cta_type !== "NONE" && (
                  <>
                    <Field label="CTA Text">
                      <input
                        className={inputClass}
                        value={form.cta_text}
                        disabled={saving}
                        placeholder="Order Now"
                        onChange={(e) =>
                          updateField("cta_text", e.target.value)
                        }
                      />
                    </Field>

                    <Field label={ctaValueLabel} required>
                      <input
                        className={inputClass}
                        value={form.cta_value}
                        disabled={saving}
                        placeholder={
                          form.cta_type === "WEBVIEW"
                            ? "https://..."
                            : "Enter value"
                        }
                        onChange={(e) =>
                          updateField("cta_value", e.target.value)
                        }
                      />
                    </Field>
                  </>
                )}
              </div>
            </Section>

            {/* =================================================
                LOCATION
            ================================================= */}

            <Section
              title="Location & Business Targeting"
              description="Control where and for which business the banner appears."
              icon={MapPin}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Target Type">
                  <select
                    className={selectClass}
                    value={form.target_type}
                    disabled={saving}
                    onChange={(e) => updateField("target_type", e.target.value)}
                  >
                    {TARGET_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </Field>

                {/* CITY */}

                {form.target_type === "CITY" && (
                  <>
                    <Field label="City ID">
                      <input
                        className={inputClass}
                        value={form.city_id}
                        disabled={saving}
                        placeholder="1"
                        onChange={(e) =>
                          updateField(
                            "city_id",
                            e.target.value.replace(/\D/g, ""),
                          )
                        }
                      />
                    </Field>

                    <Field label="City Code">
                      <input
                        className={inputClass}
                        value={form.city_code}
                        disabled={saving}
                        placeholder="DEL"
                        onChange={(e) =>
                          updateField("city_code", e.target.value)
                        }
                      />
                    </Field>
                  </>
                )}

                {/* RADIUS */}

                {form.target_type === "RADIUS" && (
                  <>
                    <Field label="Latitude" required>
                      <input
                        type="number"
                        step="any"
                        className={inputClass}
                        value={form.latitude}
                        disabled={saving}
                        placeholder="28.6263"
                        onChange={(e) =>
                          updateField("latitude", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Longitude" required>
                      <input
                        type="number"
                        step="any"
                        className={inputClass}
                        value={form.longitude}
                        disabled={saving}
                        placeholder="77.3722"
                        onChange={(e) =>
                          updateField("longitude", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Radius (KM)" required>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        className={inputClass}
                        value={form.radius_km}
                        disabled={saving}
                        placeholder="10"
                        onChange={(e) =>
                          updateField("radius_km", e.target.value)
                        }
                      />
                    </Field>
                  </>
                )}

                <Field label="Restaurant ID">
                  <input
                    className={inputClass}
                    value={form.restaurant_id}
                    disabled={saving}
                    placeholder="Required"
                    onChange={(e) =>
                      updateField("restaurant_id", e.target.value)
                    }
                  />
                </Field>

                <Field label="Category ID">
                  <input
                    className={inputClass}
                    value={form.category_id}
                    disabled={saving}
                    placeholder="Optional"
                    onChange={(e) =>
                      updateField(
                        "category_id",
                        e.target.value.replace(/\D/g, ""),
                      )
                    }
                  />
                </Field>

                <Field label="Order Type">
                  <select
                    className={selectClass}
                    value={form.order_type}
                    disabled={saving}
                    onChange={(e) => updateField("order_type", e.target.value)}
                  >
                    {ORDER_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </Section>

            {/* =================================================
                AUDIENCE
            ================================================= */}

            <Section
              title="Audience & Platform"
              description="Target devices, customer type and compatible app versions."
              icon={MonitorSmartphone}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Field label="Platform">
                  <select
                    className={selectClass}
                    value={form.platform}
                    disabled={saving}
                    onChange={(e) => updateField("platform", e.target.value)}
                  >
                    {PLATFORM_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Customer Type">
                  <select
                    className={selectClass}
                    value={form.customer_type}
                    disabled={saving}
                    onChange={(e) =>
                      updateField("customer_type", e.target.value)
                    }
                  >
                    {CUSTOMER_TYPES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Minimum App Version">
                  <input
                    className={inputClass}
                    value={form.minimum_app_version}
                    disabled={saving}
                    placeholder="1.0.0"
                    onChange={(e) =>
                      updateField("minimum_app_version", e.target.value)
                    }
                  />
                </Field>

                <Field label="Maximum App Version">
                  <input
                    className={inputClass}
                    value={form.maximum_app_version}
                    disabled={saving}
                    placeholder="Optional"
                    onChange={(e) =>
                      updateField("maximum_app_version", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>



            {/* =================================================
                ACTIONS
            ================================================= */}

            <div className="sticky bottom-3 z-10 flex flex-col gap-2 rounded-xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-800">
                  Ready to publish?
                </p>

                <p className="text-[11px] text-gray-500">
                  Review targeting and schedule before creating the banner.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleReset}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  <RotateCcw size={14} />
                  Reset
                </button>

                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSubmit}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-[#01C37D] px-5 text-xs font-semibold text-white transition hover:bg-[#00ad6f] disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}

                  {saving ? "Creating..." : "Create App Banner"}
                </button>
              </div>
            </div>
          </div>



          {/* =================================================
              PHONE PLACEMENT PREVIEW
          ================================================= */}

          <aside className="xl:sticky xl:top-5 xl:self-start">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">

              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">
                  Banner Placement
                </p>

                <p className="mt-0.5 text-[11px] text-gray-500">
                  Shows where your banner will appear in the app.
                </p>
              </div>

              <div className="flex justify-center bg-gray-50 p-4">

                {/* PHONE */}
                <div className="relative w-[270px] overflow-hidden rounded-[30px] border-[6px] border-gray-900 bg-white shadow-xl">

                  {/* NOTCH */}
                  <div className="absolute left-1/2 top-1 z-30 h-4 w-16 -translate-x-1/2 rounded-full bg-gray-900" />

                  {/* PHONE CONTENT */}
                 <div
  ref={phonePreviewScrollRef}
  className="h-[560px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
>

                    {/* HEADER */}
                    <div className="absolute left-0 right-0 top-0 z-40 px-3 py-2">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[8px] font-medium text-white/80">
                  Delivering to
                </p>
                <p className="max-w-[150px] truncate text-[10px] font-bold text-white">
                  Sector 62, Noida, Uttar Pradesh
                </p>
              </div>
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/80 backdrop-blur-sm">
                <span className="text-[10px]">👤</span>
              </div>
            </div>
          </div>


                    {/* HERO */}
                    <div
  ref={(el) => {
    phonePreviewPositionRefs.current["HOME_HERO"] = el;
  }}
>
  <PreviewPlacement
    label="Hero Banner"
    position="HOME_HERO"
    selected={form.banner_position === "HOME_HERO"}
    image={previewImage}
  />
</div>


                    {/* AFTER HERO */}
                    <div
  ref={(el) => {
    phonePreviewPositionRefs.current["AFTER_HERO"] = el;
  }}
  className="px-2 pt-2"
>
  <PreviewPlacement
    label="After Hero"
    position="AFTER_HERO"
    selected={form.banner_position === "AFTER_HERO"}
    image={previewImage}
  />
</div>


                    {/* CATEGORY FILTER */}
                    <div className="px-2 py-3">
                      <p className="mb-2 text-[10px] font-bold text-gray-900">
                        Categories
                      </p>

                      <div className="flex gap-2 overflow-hidden">
                        {[
                          { name: "Food", icon: Utensils },
                          { name: "Pizza", icon: Pizza },
                          { name: "Burgers", icon: Beef },
                          { name: "Drinks", icon: CupSoda },
                          { name: "Desserts", icon: IceCreamBowl },
                          { name: "More", icon: Sparkles },
                        ].map(({ name, icon: Icon }) => (
                          <div key={name} className="flex min-w-[40px] flex-col items-center gap-1">
                            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-gray-200 bg-gray-50 text-gray-500">
                              <Icon size={15} strokeWidth={1.8} />
                            </div>
                            <span className="max-w-[42px] truncate text-[7px] text-gray-600">
                              {name}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-2 grid grid-cols-4 gap-1">
                        {[
                          { label: "Search", icon: Search },
                          { label: "Offers", icon: Sparkles },
                          { label: "Popular", icon: Star },
                          { label: "Favourites", icon: Heart },
                        ].map(({ label, icon: Icon }) => (
                          <div
                            key={label}
                            className="flex h-7 items-center justify-center gap-1 rounded-md border border-gray-100 bg-gray-50 text-gray-500"
                          >
                            <Icon size={9} />
                            <span className="text-[6px] font-medium">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>


                    {/* AFTER CATEGORY */}
                    <div
  ref={(el) => {
    phonePreviewPositionRefs.current["AFTER_CATEGORY_FILTER"] = el;
  }}
  className="px-2"
>
  <PreviewPlacement
    label="After Category Filter"
    position="AFTER_CATEGORY_FILTER"
    selected={form.banner_position === "AFTER_CATEGORY_FILTER"}
    image={previewImage}
  />
</div>


                    {/* FAST DELIVERY */}
                    <div className="px-2 py-3">
                      <p className="mb-2 text-[10px] font-bold text-gray-900">
                        Fast Delivery
                      </p>

                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { icon: Bike, name: "Fast Delivery" },
                          { icon: Clock3, name: "Quick Bites" },
                          { icon: Utensils, name: "Fresh Food" },
                          { icon: ShoppingBag, name: "Best Picks" },
                          { icon: Star, name: "Top Rated" },
                          { icon: Heart, name: "Favourite" },
                        ].map(({ icon: Icon, name }) => (
                          <div
                            key={name}
                            className="aspect-square overflow-hidden rounded-lg border border-gray-100 bg-gray-50"
                          >
                            <div className="flex h-[72%] w-full items-center justify-center bg-gray-100 text-gray-400">
                              <Icon size={25} strokeWidth={1.5} />
                            </div>
                            <div className="p-1.5">
                              <p className="truncate text-[7px] font-semibold text-gray-700">
                                {name}
                              </p>
                              <div className="mt-1 flex items-center gap-1">
                                <Star size={6} className="fill-current text-gray-400" />
                                <span className="text-[6px] text-gray-400">4.5</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>


                    {/* AFTER FAST DELIVERY */}
                    <div
  ref={(el) => {
    phonePreviewPositionRefs.current["AFTER_FAST_DELIVERY"] = el;
  }}
  className="px-2"
>
  <PreviewPlacement
    label="After Fast Delivery"
    position="AFTER_FAST_DELIVERY"
    selected={form.banner_position === "AFTER_FAST_DELIVERY"}
    image={previewImage}
  />
</div>


                    {/* WHAT'S ON YOUR MIND */}
                    <div className="space-y-2">
                      <div className="flex h-16 w-full items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-gray-400 shadow-sm">
                          <Pizza size={19} />
                        </div>
                        <div>
                          <p className="text-[9px] font-semibold text-gray-700">Craving Pizza?</p>
                          <p className="mt-0.5 text-[7px] text-gray-400">Explore delicious pizzas</p>
                        </div>
                      </div>

                      <div className="flex h-16 w-full items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-gray-400 shadow-sm">
                          <IceCreamBowl size={19} />
                        </div>
                        <div>
                          <p className="text-[9px] font-semibold text-gray-700">Something Sweet?</p>
                          <p className="mt-0.5 text-[7px] text-gray-400">Find your favourite desserts</p>
                        </div>
                      </div>
                    </div>


                    {/* AFTER WHAT'S ON YOUR MIND */}
                    <div className="px-2">
                      <PreviewPlacement
                        label="After What's on Your Mind"
                        position="AFTER_WHAT_ON_YOUR_MIND"
                        selected={
                          form.banner_position === "AFTER_WHAT_ON_YOUR_MIND"
                        }
                        image={previewImage}
                      />
                    </div>


                    {/* FOOTER */}
                    <PreviewPlacement
                      label="Home Footer"
                      position="HOME_FOOTER"
                      selected={form.banner_position === "HOME_FOOTER"}
                      image={previewImage}
                    />

                  </div>
                </div>
              </div>


           {/* SELECTED POSITION */}
<div className="border-t border-gray-100 px-4 py-3">
  {form.screen_name && form.banner_position ? (
    <>
      <p className="text-[10px] font-medium text-gray-400">
        Selected placement
      </p>

     <p className="mt-0.5 text-xs font-bold text-gray-900">
{typeof form.banner_position === "string"
  ? form.banner_position
      .toLowerCase()
      .split("_")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ")
  : form.banner_position?.label ||
    form.banner_position?.value ||
    "—"}
</p>
    </>
  ) : (
    <p className="text-[10px] text-gray-400">
      Select a screen and banner position to see placement.
    </p>
  )}
</div>
            </div>
          </aside>
        </div>
      )}

      {/* =========================================================
    DELETE CONFIRMATION MODAL
========================================================= */}

{deleteModalOpen && bannerToDelete && (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
    <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">

      {/* HEADER */}
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
            <Trash2 size={17} className="text-red-500" />
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Delete Banner?
            </h3>

            <p className="mt-0.5 text-[11px] text-gray-500">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* BANNER PREVIEW */}
      <div className="p-5">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">

          {/* IMAGE */}
          <div className="relative aspect-[16/7] overflow-hidden bg-gray-100">
            {getBannerImage(bannerToDelete) ? (
              <img
                src={getBannerImage(bannerToDelete)}
                alt={bannerToDelete.banner_name || "Banner"}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-gray-400">
                <ImageIcon size={32} />
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="p-3">
            <div className="flex items-start justify-between gap-3">

              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-gray-900">
                  {bannerToDelete.banner_name || "Unnamed Banner"}
                </p>

                {bannerToDelete.banner_title && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {bannerToDelete.banner_title}
                  </p>
                )}
              </div>

              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-1 text-[9px] font-bold text-gray-600">
                #{bannerToDelete.pk_banner_id}
              </span>
            </div>
          </div>
        </div>

        {/* WARNING */}
        <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2.5">
          <p className="text-[11px] leading-4 text-red-600">
            Are you sure you want to permanently delete this banner?
          </p>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">

        <button
          type="button"
          disabled={deleting}
          onClick={() => {
            setDeleteModalOpen(false);
            setBannerToDelete(null);
          }}
          className="h-9 rounded-lg border border-gray-300 bg-white px-4 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={deleting}
          onClick={confirmDeleteBanner}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-red-500 px-4 text-xs font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {deleting ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              Deleting...
            </>
          ) : (
            <>
              <Trash2 size={13} />
              Delete Banner
            </>
          )}
        </button>

      </div>
    </div>
  </div>
)}
    </div>
  );
}


const PreviewPlacement = ({
  label,
  position,
  selected,
  aspect = "aspect-[16/7]",
  image,
}) => {
  return (
    <div
      className={`
        relative w-full overflow-hidden rounded-md border transition-all
        ${aspect}
        ${selected
          ? "border-[#01C37D] bg-[#01C37D]/10 ring-2 ring-[#01C37D]/20"
          : "border-gray-100 bg-gray-50"
        }
      `}
    >
      {selected && image ? (
        <img
          src={image}
          alt={`${label} preview`}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center">
          {selected ? (
            <>
              <div className="grid h-7 w-7 place-items-center rounded-full bg-[#01C37D] text-white">
                <Plus size={13} />
              </div>

              <p className="mt-1 text-[8px] font-bold text-[#01C37D]">
                Your banner
              </p>

              <p className="mt-0.5 text-[6px] text-gray-400">
                Will appear here
              </p>
            </>
          ) : (
            <p className="text-[6px] text-gray-300">
              {label}
            </p>
          )}
        </div>
      )}

      {selected && (
        <div className="absolute left-1.5 top-1.5 z-10 rounded bg-[#01C37D] px-1.5 py-0.5 text-[6px] font-bold text-white">
          SELECTED
        </div>
      )}
    </div>
  );
};
/* =========================================================
   TOP-LEVEL HELPERS (module scope, outside AppBannerList)
========================================================= */

function getBannerImage(banner) {
  if (!banner) return "";
  if (banner.thumbnail_url) return banner.thumbnail_url;
  return (
    banner.media_url?.match(/mobile:\s*(https?:\/\/[^|\s]+)/i)?.[1] ||
    banner.media_url?.match(/desktop:\s*(https?:\/\/[^|\s]+)/i)?.[1] ||
    ""
  );
}

function getBannersByPosition(banners, position) {
  return (banners || [])
    .filter(
      (banner) =>
        String(banner?.screen_name || "").toUpperCase() === "HOME" &&
        String(banner?.banner_position || "").toUpperCase() === position &&
        String(banner?.status || "").toUpperCase() === "Y"
    )
    .sort((a, b) => Number(a?.display_order || 0) - Number(b?.display_order || 0));
}

function PhoneBanner({
  banner,
  className = "",
  aspectClass = "aspect-[16/7]",
  rounded = true,
}) {
  const image = getBannerImage(banner);
  if (!image) return null;

  return (
    <div
      className={`relative overflow-hidden bg-gray-100 ${rounded ? "rounded-lg" : ""
        } ${aspectClass} ${className}`}
    >
      <img
        src={image}
        alt={banner?.banner_name || "Home banner"}
        className="block h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
  );
}


function BannerGroup({
  banners,
  position,
  aspectClass = "aspect-[16/7]",
  className = "",
  rounded = true,
  interval = 3000,
  onCreate,
}) {
  const positionBanners = getBannersByPosition(banners, position);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset to first banner whenever the banner list changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [positionBanners.length]);

  // Auto rotate only when there are multiple banners
  useEffect(() => {
    if (positionBanners.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % positionBanners.length);
    }, interval);

    return () => clearInterval(timer);
  }, [positionBanners.length, interval]);

  if (!positionBanners.length) {
    return (
      <button
        type="button"
          onClick={() => onCreate?.(position)}
        className={`group relative block w-full overflow-hidden border border-dashed border-gray-300 bg-gray-50 transition hover:border-[#01C37D] hover:bg-[#01C37D]/5 ${rounded ? "rounded-lg" : ""
          } ${aspectClass} ${className}`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="grid h-7 w-7 place-items-center rounded-full bg-[#01C37D]/10 text-[#01C37D] transition group-hover:bg-[#01C37D] group-hover:text-white">
            <Plus size={13} />
          </div>

          <p className="mt-1.5 text-[9px] font-semibold text-gray-600 group-hover:text-[#01C37D]">
            Add Banner
          </p>

          <p className="mt-0.5 max-w-[150px] text-center text-[7px] text-gray-400">
            {position.toLowerCase().replaceAll("_", " ").replace("after ", "")}
          </p>
        </div>
      </button>
    );
  }

  const banner = positionBanners[currentIndex];

  return (
    <div className={`relative ${className}`}>
      <PhoneBanner
        key={banner?.pk_banner_id}
        banner={banner}
        aspectClass={aspectClass}
        rounded={rounded}
      />

      {/* Dots only when there are multiple banners */}
      {positionBanners.length > 1 && (
        <div className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 items-center gap-1">
          {positionBanners.map((item, index) => (
            <div
              key={item?.pk_banner_id || index}
              className={`h-1 rounded-full transition-all ${index === currentIndex ? "w-3 bg-white" : "w-1 bg-white/60"
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HomePhonePreview({ banners, onCreate, phoneScrollRef }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[300px] max-w-full rounded-[32px] border-[7px] border-gray-900 bg-gray-900 p-[4px] shadow-2xl">
        {/* PHONE NOTCH */}
        <div className="absolute left-1/2 top-[5px] z-30 h-[18px] w-[78px] -translate-x-1/2 rounded-full bg-gray-900" />

        {/* PHONE SCREEN — static frame, does NOT scroll itself */}
        <div className="relative overflow-hidden rounded-[24px] bg-white">
          {/* STATUS BAR */}
          <div className="absolute left-0 right-0 top-0 z-40 flex h-7 items-center justify-between px-4 pt-1 text-[8px] font-semibold text-white">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span>●●●</span>
              <span>▰</span>
            </div>
          </div>

          {/* HOME HEADER - OVER HERO */}
          <div className="absolute left-0 right-0 top-7 z-40 px-3 py-2">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[8px] font-medium text-white/80">
                  Delivering to
                </p>
                <p className="max-w-[150px] truncate text-[10px] font-bold text-white">
                  Sector 62, Noida, Uttar Pradesh
                </p>
              </div>
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/80 backdrop-blur-sm">
                <span className="text-[10px]">👤</span>
              </div>
            </div>
          </div>

          {/* SCROLLABLE HOME CONTENT — the ONLY scroll container */}
          <div
            ref={phoneScrollRef}
            className="max-h-[600px] overflow-y-auto
              [scrollbar-width:none]
              [-ms-overflow-style:none]
              [&::-webkit-scrollbar]:hidden"
          >
            {/* HERO */}
            <BannerGroup
              banners={banners}
              position="HOME_HERO"
              aspectClass="aspect-[5/4]"
              className="!space-y-0"
              rounded={false}
              onCreate={onCreate}
            />

            {/* AFTER HERO */}
            <div className="px-2 pt-2">
              <BannerGroup
                banners={banners}
                position="AFTER_HERO"
                aspectClass="aspect-[16/7]"
                onCreate={onCreate}
              />
            </div>

            {/* CATEGORY FILTER */}
            <div className="px-2 py-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] font-bold text-gray-900">Categories</p>
                <span className="text-[8px] font-medium text-[#01C37D]">See all</span>
              </div>

              <div className="flex gap-3 overflow-hidden">
                {[
                  { name: "Food", icon: Utensils },
                  { name: "Pizza", icon: Pizza },
                  { name: "Burgers", icon: Beef },
                  { name: "Drinks", icon: CupSoda },
                  { name: "Desserts", icon: IceCreamBowl },
                  { name: "More", icon: Sparkles },
                ].map(({ name, icon: Icon }) => (
                  <div key={name} className="flex min-w-[40px] flex-col items-center gap-1">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-gray-200 bg-gray-50 text-gray-500">
                      <Icon size={15} strokeWidth={1.8} />
                    </div>
                    <span className="max-w-[42px] truncate text-[7px] text-gray-600">
                      {name}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-3 grid grid-cols-4 gap-1.5">
                {[
                  { label: "Search", icon: Search },
                  { label: "Offers", icon: Sparkles },
                  { label: "Popular", icon: Star },
                  { label: "Favourites", icon: Heart },
                ].map(({ label, icon: Icon }) => (
                  <div
                    key={label}
                    className="flex h-7 items-center justify-center gap-1 rounded-md border border-gray-100 bg-gray-50 text-gray-500"
                  >
                    <Icon size={9} />
                    <span className="text-[6px] font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* AFTER CATEGORY FILTER */}
            <div className="px-2">
              <BannerGroup
                banners={banners}
                position="AFTER_CATEGORY_FILTER"
                aspectClass="aspect-[16/7]"
                onCreate={onCreate}
              />
            </div>

            {/* FAST DELIVERY */}
            <div className="px-2 py-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] font-bold text-gray-900">Fast Delivery</p>
                <span className="text-[8px] font-medium text-[#01C37D]">See all</span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Bike, name: "Fast Delivery" },
                  { icon: Clock3, name: "Quick Bites" },
                  { icon: Utensils, name: "Fresh Food" },
                  { icon: ShoppingBag, name: "Best Picks" },
                  { icon: Star, name: "Top Rated" },
                  { icon: Heart, name: "Favourite" },
                ].map(({ icon: Icon, name }) => (
                  <div
                    key={name}
                    className="aspect-square overflow-hidden rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <div className="flex h-[72%] w-full items-center justify-center bg-gray-100 text-gray-400">
                      <Icon size={25} strokeWidth={1.5} />
                    </div>
                    <div className="p-1.5">
                      <p className="truncate text-[7px] font-semibold text-gray-700">
                        {name}
                      </p>
                      <div className="mt-1 flex items-center gap-1">
                        <Star size={6} className="fill-current text-gray-400" />
                        <span className="text-[6px] text-gray-400">4.5</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AFTER FAST DELIVERY */}
            <div className="px-2">
              <BannerGroup
                banners={banners}
                position="AFTER_FAST_DELIVERY"
                aspectClass="aspect-[16/7]"
                onCreate={onCreate}
              />
            </div>

            {/* WHAT'S ON YOUR MIND */}
            <div className="space-y-2">
              <div className="flex h-16 w-full items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-gray-400 shadow-sm">
                  <Pizza size={19} />
                </div>
                <div>
                  <p className="text-[9px] font-semibold text-gray-700">Craving Pizza?</p>
                  <p className="mt-0.5 text-[7px] text-gray-400">Explore delicious pizzas</p>
                </div>
              </div>

              <div className="flex h-16 w-full items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-gray-400 shadow-sm">
                  <IceCreamBowl size={19} />
                </div>
                <div>
                  <p className="text-[9px] font-semibold text-gray-700">Something Sweet?</p>
                  <p className="mt-0.5 text-[7px] text-gray-400">Find your favourite desserts</p>
                </div>
              </div>
            </div>

            {/* AFTER WHAT'S ON YOUR MIND */}
            <div className="px-2">
              <BannerGroup
                banners={banners}
                position="AFTER_WHAT_ON_YOUR_MIND"
                aspectClass="aspect-[16/7]"
                onCreate={onCreate}
              />
            </div>

            {/* HOME FOOTER */}
            <div className="p-2 pb-4">
              <BannerGroup
                banners={banners}
                position="HOME_FOOTER"
                aspectClass="aspect-[16/7]"
                className="!space-y-0"
                rounded={false}
                onCreate={onCreate}
              />
            </div>

            {/* BOTTOM NAV */}
            <div className="sticky bottom-0 flex h-12 items-center justify-around border-t border-gray-100 bg-white">
              {["Home", "Search", "Orders", "Profile"].map((item, index) => (
                <div
                  key={item}
                  className={`flex flex-col items-center gap-0.5 ${index === 0 ? "text-[#01C37D]" : "text-gray-400"
                    }`}
                >
                  <div className="h-4 w-4 rounded-full bg-current opacity-70" />
                  <span className="text-[7px] font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   APP BANNER LIST
========================================================= */

function AppBannerList({ banners = [], loading = false, onCreate,onDelete }) {
  const phoneScrollRef = useRef(null);

  /* Restore phone scroll position */
  useEffect(() => {
    const el = phoneScrollRef.current;
    if (!el) return;

    const saved = sessionStorage.getItem("app-banner-phone-scroll");
    if (saved !== null) {
      requestAnimationFrame(() => {
        el.scrollTop = Number(saved);
      });
      // extra safety net for late-loading images
      const t = setTimeout(() => {
        el.scrollTop = Number(saved);
      }, 150);
      return () => clearTimeout(t);
    }
  }, []);

  /* Save phone scroll position */
  useEffect(() => {
    const el = phoneScrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      sessionStorage.setItem("app-banner-phone-scroll", String(el.scrollTop));
    };

    el.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", handleScroll);
      sessionStorage.setItem("app-banner-phone-scroll", String(el.scrollTop));
    };
  }, []);

  /* ---------------- LOADING ---------------- */

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <Loader2 size={24} className="animate-spin text-[#01C37D]" />
          <p className="text-xs font-medium">Loading app banners...</p>
        </div>
      </div>
    );
  }

  /* ---------------- EMPTY ---------------- */

  if (!banners.length) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
          <ImageIcon size={22} />
        </div>

        <h3 className="mt-3 text-sm font-semibold text-gray-900">
          No app banners found
        </h3>

        <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
          Create your first targeted app banner. Once created, it will appear
          here.
        </p>

        <button
          type="button"
          onClick={onCreate}
          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#01C37D] px-4 text-xs font-semibold text-white transition hover:bg-[#00ad6f]"
        >
          <Plus size={14} />
          Create Banner
        </button>
      </div>
    );
  }

  /* =========================================================
     EXISTING BANNER LIST
  ========================================================= */

  return (
    <div>
      {/* HEADER */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">App Banners</p>
          <p className="mt-0.5 text-[11px] text-gray-500">
            {banners.length} banner{banners.length !== 1 ? "s" : ""} found
          </p>
        </div>

        <button
          type="button"
          onClick={onCreate}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-[#01C37D] px-4 text-xs font-semibold text-white transition hover:bg-[#00ad6f]"
        >
          <Plus size={14} />
          Create Banner
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_650px]">
        {/* LEFT - BANNER LIST */}
        <div className="min-w-0">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-2">
            {banners.map((banner, index) => (
              <AppBannerCard
                key={banner?.pk_banner_id || `${banner?.banner_name}-${index}`}
                banner={banner}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>

        {/* RIGHT - HOME PHONE PREVIEW */}
        <aside className="xl:sticky xl:top-5 xl:self-start">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">
                Home Screen Preview
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                Live preview using active HOME banners from API
              </p>
            </div>

            <div className="flex justify-center bg-gray-50 px-4 py-6">
              <HomePhonePreview
                banners={banners}
                onCreate={onCreate}
                phoneScrollRef={phoneScrollRef}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}



/* =========================================================
   BANNER CARD
========================================================= */

function AppBannerCard({ banner,onDelete }) {

  const imageUrl =
    banner?.thumbnail_url ||
    banner?.media_url
      ?.match(/mobile:\s*(https?:\/\/[^|\s]+)/i)?.[1] ||
    "";
  const active = String(banner?.status || "").toUpperCase() === "Y";

  return (
    <div className="group overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      {/* =====================================================
          IMAGE
      ===================================================== */}

      <div
        className="relative aspect-[16/7] overflow-hidden bg-gray-100"
        style={{
          backgroundColor: banner?.background_color || "#f3f4f6",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={banner?.banner_name || "App Banner"}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            <ImageIcon size={28} />
          </div>
        )}

        {/* STATUS */}

        <span
          className={`absolute right-2 top-2 rounded-full px-2 py-1 text-[9px] font-bold shadow-sm ${active ? "bg-[#01C37D] text-white" : "bg-gray-700 text-white"
            }`}
        >
          {active ? "ACTIVE" : "INACTIVE"}
        </span>

        {/* MEDIA TYPE */}

        <span className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-1 text-[9px] font-semibold text-white backdrop-blur-sm">
          {banner?.media_type || "IMAGE"}
        </span>
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div className="p-3">
        {/* TITLE */}

        {/* TITLE + DELETE */}
<div className="flex items-start justify-between gap-2">
  <div className="min-w-0">
    <p className="truncate text-sm font-bold text-gray-900">
      {banner?.banner_name || "—"}
    </p>

    {banner?.banner_title && (
      <p className="mt-0.5 truncate text-xs font-semibold text-gray-700">
        {banner.banner_title}
      </p>
    )}
  </div>

  <div className="flex shrink-0 items-center gap-1.5">
    {/* Banner ID */}
    <span className="rounded-md bg-gray-100 px-2 py-1 text-[9px] font-bold text-gray-600">
      #{banner?.pk_banner_id || "—"}
    </span>

    {/* Delete Button */}
    <button
      type="button"
      onClick={() => onDelete?.(banner)}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-red-100 bg-red-50 text-red-500 transition hover:border-red-200 hover:bg-red-100 hover:text-red-600"
      title="Delete banner"
      aria-label={`Delete ${banner?.banner_name || "banner"}`}
    >
      <Trash2 size={13} />
    </button>
  </div>
</div>

        {/* SUBTITLE */}

        {banner?.banner_subtitle && (
          <p className="mt-2 line-clamp-2 min-h-[32px] text-[11px] leading-4 text-gray-500">
            {banner.banner_subtitle}
          </p>
        )}

        {/* =================================================
            CTA
        ================================================= */}

        {banner?.cta_type && banner?.cta_type !== "NONE" && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2.5 py-2">
            <div className="min-w-0">
              <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                Action
              </p>

              <p className="mt-0.5 truncate text-[10px] font-semibold text-gray-700">
                {banner.cta_type}
              </p>
            </div>

            {banner?.cta_text && (
              <span className="shrink-0 rounded-md bg-[#01C37D]/10 px-2 py-1 text-[9px] font-bold text-[#009c64]">
                {banner.cta_text}
              </span>
            )}
          </div>
        )}

        {/* =================================================
            TARGETING
        ================================================= */}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <BannerInfo
            icon={MapPinned}
            label="Target"
            value={banner?.target_type || "GLOBAL"}
          />

          <BannerInfo
            icon={Smartphone}
            label="Platform"
            value={banner?.platform || "ALL"}
          />

          <BannerInfo
            icon={Settings2}
            label="Position"
            value={banner?.banner_position || "—"}
          />

          <BannerInfo
            icon={CalendarClock}
            label="Order"
            value={banner?.order_type?.replaceAll("_", " ") || "ALL"}
          />
        </div>

        {/* =================================================
            LOCATION DETAIL
        ================================================= */}

        {banner?.target_type === "CITY" && (
          <div className="mt-2 rounded-lg bg-gray-50 p-2">
            <p className="text-[9px] text-gray-400">City</p>

            <p className="mt-0.5 text-[10px] font-semibold text-gray-700">
              {banner?.city_code || "—"}

              {banner?.city_id ? ` • ID ${banner.city_id}` : ""}
            </p>
          </div>
        )}

        {banner?.target_type === "RADIUS" && (
          <div className="mt-2 rounded-lg bg-gray-50 p-2">
            <p className="text-[9px] text-gray-400">Radius Target</p>

            <p className="mt-0.5 truncate text-[10px] font-semibold text-gray-700">
              {banner?.latitude || "—"}, {banner?.longitude || "—"}
              {banner?.radius_km ? ` • ${banner.radius_km} KM` : ""}
            </p>
          </div>
        )}

        {/* =================================================
            DISPLAY / PRIORITY
        ================================================= */}

        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
          <div>
            <p className="text-[9px] text-gray-400">Display Order</p>

            <p className="mt-0.5 text-xs font-bold text-gray-700">
              {banner?.display_order ?? 0}
            </p>
          </div>

          <div className="text-center">
            <p className="text-[9px] text-gray-400">Screen</p>

            <p className="mt-0.5 text-xs font-bold text-gray-700">
              {banner?.screen_name || "—"}
            </p>
          </div>

          <div className="text-right">
            <p className="text-[9px] text-gray-400">Priority</p>

            <p className="mt-0.5 text-xs font-bold text-gray-700">
              {banner?.priority ?? 0}
            </p>
          </div>
        </div>

        {/* =================================================
            SCHEDULE
        ================================================= */}

        {(banner?.start_date || banner?.end_date) && (
          <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600">
              <CalendarDays size={12} />
              Schedule
            </div>

            <div className="mt-1.5 flex items-center justify-between gap-2 text-[9px] text-gray-500">
              <span>{formatBannerDate(banner?.start_date)}</span>

              <span>→</span>

              <span className="text-right">
                {formatBannerDate(banner?.end_date)}
              </span>
            </div>
          </div>
        )}

        {/* =================================================
            CREATED DATE
        ================================================= */}

        {banner?.created_at && (
          <p className="mt-2 text-right text-[9px] text-gray-400">
            Created {formatBannerDate(banner.created_at)}
          </p>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   INFO BOX
========================================================= */

function BannerInfo({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 p-2">
      <div className="flex items-center gap-1 text-[9px] text-gray-400">
        <Icon size={10} />

        {label}
      </div>

      <p className="mt-1 truncate text-[10px] font-semibold text-gray-700">
        {value || "—"}
      </p>
    </div>
  );
}

/* =========================================================
   DATE FORMAT
========================================================= */

function formatBannerDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
